import { filterServiceRequestsByTUSSProcedimentosEventos } from "@/services/fhir/servicerequest/hooks/useServiceRequestByOrganization";
import { patientDisplayName } from "src-next/modules/clinical-insights/helpers/insightPractitionerUtils";
import type { InsightChartItem, InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

const STATUS_TRANSLATION: Record<string, string> = {
  active: "Ativo",
  "on-hold": "Suspenso",
  revoked: "Cancelado",
  completed: "Concluído",
  "entered-in-error": "Erro",
  draft: "Solicitado",
  unknown: "Desconhecido",
};

function srDisplay(sr: fhir5.ServiceRequest): { code: string; label: string } {
  let code = "unknown";
  let display = "Procedimento não especificado";
  if (sr.code?.concept?.coding?.[0]) {
    code = sr.code.concept.coding[0].code || "unknown";
    display = sr.code.concept.coding[0].display || "Procedimento não especificado";
  }
  return { code: display, label: display };
}

function practitionerName(sr: fhir5.ServiceRequest): string {
  if (sr.requester?.display) return sr.requester.display;
  const pract = sr.contained?.find((c) => c.resourceType === "Practitioner");
  if (pract?.resourceType === "Practitioner" && pract.name?.[0]?.text) {
    return pract.name[0].text;
  }
  return "Profissional não especificado";
}

function formatDatePt(date?: string): string {
  if (!date) return "-";
  try {
    return new Date(date).toLocaleDateString("pt-BR");
  } catch {
    return date;
  }
}

export function buildServiceRequestInsightCharts(
  bundle: { serviceRequests?: fhir5.ServiceRequest[] },
  dict: {
    serviceRequestStatus: Record<string, string>;
    serviceRequestPriority: Record<string, string>;
  }
) {
  const filtered = filterServiceRequestsByTUSSProcedimentosEventos(
    bundle.serviceRequests || []
  );

  const statusMap = new Map<string, number>();
  const displayMap = new Map<string, number>();

  for (const sr of filtered) {
    const status = sr.status || "unknown";
    statusMap.set(status, (statusMap.get(status) || 0) + 1);
    const { label } = srDisplay(sr);
    displayMap.set(label, (displayMap.get(label) || 0) + 1);
  }

  const statusItems: InsightChartItem[] = Array.from(statusMap.entries())
    .map(([key, value]) => ({
      key,
      label:
        STATUS_TRANSLATION[key] ||
        dict.serviceRequestStatus[key] ||
        key,
      value,
    }))
    .sort((a, b) => b.value - a.value);

  const rankingItems: InsightChartItem[] = Array.from(displayMap.entries())
    .map(([key, value]) => ({ key, label: key, value }))
    .sort((a, b) => b.value - a.value);

  return { statusItems, rankingItems, total: filtered.length, filtered };
}

export function buildServiceRequestPatientRows(
  filtered: fhir5.ServiceRequest[],
  patients: fhir5.Patient[],
  dict: { serviceRequestStatus: Record<string, string> }
): InsightPatientRow[] {
  const patientMap = new Map<string, fhir5.Patient>();
  for (const patient of patients) {
    if (patient.id) patientMap.set(patient.id, patient);
  }

  const groups = new Map<string, InsightPatientRow>();
  for (const sr of filtered) {
    const patientId = sr.subject?.reference?.replace("Patient/", "") || "";
    if (!patientId) continue;

    const { label } = srDisplay(sr);
    const statusKey = sr.status || "unknown";
    const normalizedStatus = statusKey.toLowerCase().replace(/[^a-z]/g, "");

    if (!groups.has(patientId)) {
      groups.set(patientId, {
        patientId,
        patientName: patientDisplayName(patientMap.get(patientId)),
        patient: patientMap.get(patientId),
        items: [],
      });
    }
    groups.get(patientId)!.items.push({
      itemKey: label,
      label,
      statusKey,
      statusLabel:
        dict.serviceRequestStatus[statusKey] ||
        dict.serviceRequestStatus[normalizedStatus] ||
        STATUS_TRANSLATION[statusKey] ||
        statusKey,
      practitioner: practitionerName(sr),
      date: formatDatePt(sr.authoredOn),
    });
  }

  return Array.from(groups.values()).sort((a, b) => b.items.length - a.items.length);
}

export { STATUS_TRANSLATION as SERVICE_REQUEST_STATUS_TRANSLATION };
